<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToTableBody'
});
</script>
<template>
	<tbody ref="$el" class="to-table-body">
		<template v-for="(row, index) in visibleRows" :key="index">
			<tr class="to-table-body-row">
				<template v-for="column in props.columnData">
					<td v-if="column.type === 'check'" :key="column.prop + 'check'" class="to-table-td type-check">
						<ToCheckbox v-model="row._checked" @change="change"></ToCheckbox>
					</td>
					<td v-else-if="column.type === 'radio'" :key="column.prop + 'radio'" class="to-table-td type-check">
						<div class="to-table-radio" :class="{ 'is-checked': row._checked }" @click="radioChange(row)"></div>
					</td>
					<td v-else-if="column.type === 'action'" :key="column.prop + 'action'" class="to-table-td type-action">
						<div class="to-table-action">
							<slot :name="column.prop" :row="row" :column="column" :index="index">
								{{ row[column.prop] }}
							</slot>
						</div>
					</td>
					<template v-else-if="column._show !== false">
						<td v-if="tipShow === index && column.showTip" :key="column.prop" v-tip="row[column.prop]" class="to-table-td" :class="setTdClass(column).value" :style="{ paddingLeft: column.toggler && row._level ? `${row._level + 2}em` : '' }" @mouseenter="e => handleMouseEnter(e, index)" @mouseleave="handleMouseLeave">
							<span v-if="hasChildren(row) && column.toggler" class="to-table-toggle" :class="{ 'is-expand': expandedSet.has(row._id) }" @click="toggleExpand(row)"></span>
							<span v-if="column.detailToggle" class="to-table-detail-toggle" :class="{ 'is-detail': row.detailShow }" @click="toggleDetail(row, index)"></span>
							<slot :name="column.prop" :row="row" :column="column" :index="index">
								<span>{{ row[column.prop] }}</span>
							</slot>
						</td>
						<td v-else :key="column.prop + 'else'" class="to-table-td" :class="setTdClass(column).value" :style="{ paddingLeft: column.toggler && row._level ? `${row._level + 2}em` : '' }" @mouseenter="e => handleMouseEnter(e, index)" @mouseleave="handleMouseLeave">
							<span v-if="hasChildren(row) && column.toggler" class="to-table-toggle" :class="{ 'is-expand': expandedSet.has(row._id) }" @click="toggleExpand(row)"></span>
							<span v-if="column.detailToggle" class="to-table-detail-toggle" :class="{ 'is-detail': row.detailShow }" @click="toggleDetail(row, index)"></span>
							<slot :name="column.prop" :row="row" :column="column" :index="index">
								<span>{{ row[column.prop] }}</span>
							</slot>
						</td>
					</template>
				</template>
			</tr>
			<tr v-if="row.detailShow" class="to-table-detail-row">
				<td :colspan="props.columnData.length" class="to-table-detail-cell">
					<slot name="detail" :row="row" :index="index"></slot>
				</td>
			</tr>
		</template>
	</tbody>
</template>
<script setup lang="ts">
import { computed, ref, reactive, nextTick, inject } from 'vue'
import { TableProvideType } from './table.vue'

const emit = defineEmits(['change'])
interface Props {
	type?: string
	data?: Record<string, any>[]
	columnData?: { prop?: string; label?: string; type?: string; toggler?: boolean; _show: boolean; showTip?: boolean; detailToggle?: boolean }[]
}
const props = withDefaults(defineProps<Props>(), {
	type: '',
	data: () => [],
	columnData: () => []
})
const $el = ref(null)
const flatData = computed(() => flattenTree(props.data))
const expandedSet = ref(new Set<number>())
const visibleRows = computed(() => flatData.value.filter(item => item._level === 0 || isVisible(item, flatData.value)))
const tipShow = ref<string | number>('')

// 选择
const checkedRows = computed(() => {
	let checked = []
	visibleRows.value.map(item => {
		if (item._checked) {
			checked.push(item)
		}
	})
	return checked
})

const allChecked = computed(() => {
	return checkedRows.value.length === flatData.value.length
})

let uid = 0
interface FlatNode {
	_id: number
	_parentId?: number
	_level: number
	children?: any[]
	_checked: boolean
	_show: boolean
	detailShow: boolean
	[key: string]: any
}
function flattenTree(data: any[], _level = 0, _parentId?: number): FlatNode[] {
	const result: FlatNode[] = []
	for (const item of data) {
		const _id = uid++
		const { children, ...rest } = item
		result.push(reactive({ ...rest, _id, _parentId, _level, _checked: Boolean(item._checked) }))

		if (children && Array.isArray(children)) {
			result.push(...flattenTree(children, _level + 1, _id))
		}
	}
	return result
}
function isVisible(item: FlatNode, all: FlatNode[]) {
	// 只有它的所有祖先都被展开，才能显示
	let _parentId = item._parentId
	while (_parentId !== undefined) {
		if (!expandedSet.value.has(_parentId)) return false
		const parent = all.find(n => n.id === _parentId)
		_parentId = parent?._parentId
	}
	return true
}

function toggleExpand(row: FlatNode) {
	if (expandedSet.value.has(row._id)) {
		expandedSet.value.delete(row._id)
	} else {
		expandedSet.value.add(row._id)
	}
}

function hasChildren(row: FlatNode) {
	return flatData.value.some(item => item._parentId === row._id)
}

function change(checked) {
	nextTick(() => {
		emit('change', checkedRows.value, checked, allChecked.value)
	})
}

function checkAll(boolean) {
	visibleRows.value.map(item => {
		item._checked = boolean
	})
}

function radioChange(row) {
	if (!row._checked) {
		visibleRows.value.map(item => {
			item._checked = false
		})
		row._checked = true
		emit('change', row, true)
	} else {
		row._checked = false
		emit('change', null, false)
	}
}

const setTdClass = column => {
	return computed(() => {
		const classes: string[] = []
		if (column.type === 'action') classes.push('is-action')
		if (column.wrap) {
			classes.push('is-wrap')
		} else if (column.wrap === false) {
			classes.push('is-nowrap')
		}
		if (column.type) classes.push(`type-${column.type}`)
		return classes
	})
}

function handleMouseEnter(e, idx) {
	if (e.target.scrollWidth > e.target.clientWidth) {
		tipShow.value = idx
	}
}

function handleMouseLeave() {
	tipShow.value = ''
}

function setCheckData(ids, valueKeys) {
	visibleRows.value.map(item => {
		if (ids) {
			if (Array.isArray(ids)) {
				if (ids.includes(item[valueKeys])) {
					item._checked = true
				} else {
					item._checked = false
				}
			} else {
				if (item[valueKeys] === ids) {
					item._checked = true
				} else {
					item._checked = false
				}
			}
		} else {
			item._checked = false
		}
	})
}
const tableRef: TableProvideType = inject('TableRef')
function toggleDetail(row, index) {
	if (row.detailShow) {
		row.detailShow = false
	} else {
		row.detailShow = true
	}
	tableRef.hanldeDetailChange(row, index, row.detailShow)
}

defineExpose({
	checkAll,
	flatData,
	setCheckData
})
</script>
